import { Button, Dialog, DialogActions, DialogContent, DialogContentText, DialogTitle, Tooltip } from '@mui/material';
import makeStyles from '@mui/styles/makeStyles';
import { useSnackbar } from 'notistack';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { MdClose, MdPhoneIphone, MdPreview } from 'react-icons/md';
import { useDispatch, useSelector } from 'react-redux';
import { ResizableBox } from 'react-resizable';

import { accountsSlice } from 'app/store';
import ProcedureFlow, { layoutEngines } from 'components/ProcedureFlow';
import {
    getProcedureCreateNodeData,
    getProcedureDirty,
    getProcedureUnsavedData,
    setOriginalProcedureData,
    setProcedureCreateNodeData,
    setProcedureDirty,
    updateProcedure,
} from 'features/procedures/proceduresSlice';
import { contentService, procedureService } from 'features/services';
import { Procedure } from 'GeminiViewerComponent/components/Procedure';
import {
    setActiveAssetId,
    setActiveAssetProcedureId,
    setChecklistInitialState,
    setDuplicateFields,
} from 'GeminiViewerComponent/components/Procedure/features/checklistDataSlice';
import {
    copyField,
    fetchProcedure,
    getAllProcedureVariables,
    getCurrentNode,
    getUniqueVariableName,
    gotoNode,
    setHeaderVariables,
    setProcedureState,
    sortNodes,
} from 'GeminiViewerComponent/components/Procedure/features/procedureSlice';
import { ProcedureNodeTypes } from 'GeminiViewerComponent/components/Procedure/ProcedureNodeTypes';
import { SnackbarDismiss } from 'GeminiViewerComponent/components/SnackbarDismiss';
import { activateLoading, deactivateLoading } from 'GeminiViewerComponent/features/globals/loadingProgressSlice';
import { cloneDeep, last } from 'GeminiViewerComponent/helpers/lodashUtils';
import { NodeTypeIcons, Role } from 'helpers';
import { useZoneNav } from 'shared/hooks/useZoneNav';
import { NodeCategoriesModal } from './components/NodeCategoriesModal';
import { NodeEditor } from './components/NodeEditor';
import { NodeManagerOptions } from './components/NodeManagerOptions';
import NodeTable from './components/NodeTable';
import NodeTableViewOnly from './components/NodeTableViewOnly';
import NodeTypeButton from './components/NodeTypeButton';
import { PanelResize } from './components/PanelResize';
import { ProcedureFlowSettings } from './ProcedureFlowSettings';
import ProcedurePropsForm from './ProcedurePropsForm';
import { makeProcedureStyles } from './styles';
import NodeInitialViewListener from './components/NodeInitialViewListener';
import DuplicateNodeDialog from './components/DuplicateNodeDialog';

const treeRanker = 'network-simplex';

// Node types whose insertion or duplication may affect non-visible execution flow.
const FLOW_IMPACTING_TYPES = new Set([
    ProcedureNodeTypes.logic.type,
    ProcedureNodeTypes.branch.type,
    ProcedureNodeTypes.email.type,
    ProcedureNodeTypes.navigationAction.type,
    ProcedureNodeTypes.contentViewAction.type,
]);

const isFlowImpacting = (nodeType) => FLOW_IMPACTING_TYPES.has(nodeType);
const hasNodeTitle = (node) => String(node?.title ?? '').trim().length > 0;
const getValidatedStartNodeId = (startNodeId, nodes) => {
    const orderedNodes = sortNodes([...(Array.isArray(nodes) ? nodes : [])]);

    if (orderedNodes.length === 0) {
        return null;
    }

    const startNode = orderedNodes.find(
        (node) => String(node?.id) === String(startNodeId)
    );

    return startNode?.id ?? orderedNodes[0].id;
};

const getEffectiveStartNodeId = (procedure) =>
    getValidatedStartNodeId(
        procedure?.start_node_id,
        procedure?.nodes
    );

const makeResizableStyles = makeStyles(() => ({
    resizable: {
        '& .react-resizable': {
            position: 'relative',
        },
        '& .react-resizable-handle': {
            position: 'absolute',
            width: '20px',
            height: '20px',
            backgroundRepeat: 'no-repeat',
            backgroundOrigin: 'content-box',
            boxSizing: 'border-box',
            backgroundImage:
                'url("data:image/svg+xml;base64,PHN2ZyB4bWxucz0iaHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmciIHZpZXdCb3g9IjAgMCA2IDYiIHN0eWxlPSJiYWNrZ3JvdW5kLWNvbG9yOiNmZmZmZmYwMCIgeD0iMHB4IiB5PSIwcHgiIHdpZHRoPSI2cHgiIGhlaWdodD0iNnB4Ij48ZyBvcGFjaXR5PSIwLjMwMiI+PHBhdGggZD0iTSA2IDYgTCAwIDYgTCAwIDQuMiBMIDQgNC4yIEwgNC4yIDQuMiBMIDQuMiAwIEwgNiAwIEwgNiA2IEwgNiA2IFoiIGZpbGw9IiMwMDAwMDAiLz48L2c+PC9zdmc+")',
            backgroundPosition: 'bottom right',
            padding: '0 3px 3px 0',
        },
        '& .react-resizable-handle-sw': {
            bottom: '0',
            left: '0',
            cursor: 'sw-resize',
            transform: 'rotate(90deg)',
        },
        '& .react-resizable-handle-se': {
            bottom: '0',
            right: '0',
            cursor: 'se-resize',
        },
        '& .react-resizable-handle-nw': {
            top: '0',
            left: '0',
            cursor: 'nw-resize',
            transform: 'rotate(180deg)',
        },
        '& .react-resizable-handle-ne': {
            top: '0',
            right: '0',
            cursor: 'ne-resize',
            transform: 'rotate(270deg)',
        },
        '& .react-resizable-handle-w, .react-resizable-handle-e': {
            top: '50%',
            marginTop: '-10px',
            cursor: 'ew-resize',
        },
        '& .react-resizable-handle-w': {
            left: '0',
            transform: 'rotate(135deg)',
        },
        '& .react-resizable-handle-e': {
            right: '0',
            transform: 'rotate(315deg)',
        },
        '& .react-resizable-handle-n, .react-resizable-handle-s': {
            left: '50%',
            marginLeft: '-10px',
            cursor: 'ns-resize',
        },
        '& .react-resizable-handle-n': {
            top: '0',
            transform: 'rotate(225deg)',
        },
        '& .react-resizable-handle-s': {
            bottom: '0',
            transform: 'rotate(45deg)',
        },
    },
}));

const computeTargetInfoFromAction = (node, label, action, nodes, parentId = null) => {
    // Compute the potential next node. This is intended to be used
    // for previewing the node flow. We can only know the next node for
    // actions that move forward in the flow. For actions the move back or
    // return to previous nodes, we can't know the actual node without the
    // user navigating the node tree

    if (action?.type === 'sub') {
        return null;
    }
    switch (action?.id) {
        case 'submit':
        case 'next': {
            // Find next in the current
            const index = nodes.findIndex((item) => item.id === node.id);
            if (index !== -1 && index < nodes.length - 1) {
                return {
                    label: label ? label : undefined,
                    nodeId: parentId ? parentId + ':' + nodes[index + 1].id : nodes[index + 1].id,
                };
            } else {
                return null;
            }
        }
        case 'back':
            // Don't know for sure the previous node since
            // user isn't actually navigating the tree
            return null;
        case 'previous':
            // Don't know for sure the previous node since
            // user isn't actually navigating the tree
            return null;
        default:
            if (typeof action?.id === 'number' || (typeof action?.id === 'string' && !isNaN(parseInt(action?.id)))) {
                return {
                    label: label ? label : undefined,
                    nodeId: parentId ? parentId + ':' + action?.id : action?.id,
                };
            } else {
                // We can't determine the last display node without the user
                // actually navigating the tree since we depend on storing the
                // last display node as the user navigates.
                return null;
            }
    }
};

const getNodeConnections = (node, nodes, parentId = null) => {
    let connections = [];
    let getOptions = false;
    let newConnections = null;
    if (node?.type === ProcedureNodeTypes.content.type) {
        if (node?.answer_group === 'select') {
            getOptions = true;
            if (Array.isArray(node?.select_options) && node?.select_options?.length > 0) {
                newConnections = node.select_options?.map((sel, idx) => {
                    const targetId = computeTargetInfoFromAction(
                        node,
                        sel?.answer_value ? sel?.answer_value : `Select ${idx}`,
                        sel.action,
                        nodes,
                        parentId
                    );
                    return targetId;
                });
                if (Array.isArray(newConnections)) {
                    connections = [...connections, ...newConnections];
                }
                if (
                    node?.multiple_answers_allow === true &&
                    Array.isArray(node?.select_target_combinations) &&
                    node?.select_target_combinations?.length > 0
                ) {
                    newConnections = node.select_target_combinations?.map((sel) => {
                        if (Array.isArray(sel?.options_required) && sel?.options_required?.length > 0) {
                            const targetId = computeTargetInfoFromAction(
                                node,
                                sel?.answer_value
                                    ? sel?.answer_value
                                    : `Select ${sel?.options_required?.map((opt) => opt?.option_id).join(',')}`,
                                sel.action,
                                nodes,
                                parentId
                            );
                            return targetId;
                        }
                        return null;
                    });
                    if (Array.isArray(newConnections)) {
                        connections = [...connections, ...newConnections];
                    }
                }
            }
        } else if (node?.answer_group === 'image') {
            getOptions = true;
            if (Array.isArray(node?.image_options) && node?.image_options?.length > 0) {
                newConnections = node.image_options?.map((img, idx) => {
                    const targetId = computeTargetInfoFromAction(
                        node,
                        img?.answer_value ? img?.answer_value : `Image ${idx}`,
                        img.action,
                        nodes,
                        parentId
                    );
                    return targetId;
                });
                if (Array.isArray(newConnections)) {
                    connections = [...connections, ...newConnections];
                }
                if (
                    node?.multiple_answers_allow === true &&
                    Array.isArray(node?.image_target_combinations) &&
                    node?.image_target_combinations?.length > 0
                ) {
                    newConnections = node.image_target_combinations?.map((img) => {
                        if (Array.isArray(img?.options_required) && img?.options_required?.length > 0) {
                            const targetId = computeTargetInfoFromAction(
                                node,
                                img?.answer_value
                                    ? img?.answer_value
                                    : `Image ${img?.options_required?.map((opt) => opt?.img_id).join(',')}`,
                                img.action,
                                nodes,
                                parentId
                            );
                            return targetId;
                        }
                        return null;
                    });
                    if (Array.isArray(newConnections)) {
                        connections = [...connections, ...newConnections];
                    }
                }
            }
        }
    }
    if (getOptions === false) {
        connections =
            node.option_buttons?.map((button) => {
                const targetId = computeTargetInfoFromAction(node, button.button_text, button.action, nodes, parentId);
                return targetId;
            }) ?? [];
    }

    if (node.next_button) {
        const nextNodeId = computeTargetInfoFromAction(node, 'next', { id: 'next' }, nodes, parentId);
        if (nextNodeId) {
            connections.push(nextNodeId);
        }
    }
    if (node.type === 'logic') {
        connections.push(computeTargetInfoFromAction(node, 'pass', node?.pass_target_node_id, nodes, parentId));
        connections.push(computeTargetInfoFromAction(node, 'fail', node?.fail_target_node_id, nodes, parentId));
    }

    if (node.action?.id) {
        connections.push(computeTargetInfoFromAction(node, node.action.id, node.action, nodes, parentId));
    }

    connections = connections.filter((e) => e != null);

    return connections;
};

const generateProcedureFlow = (startNodeId, nodes, ranker, showBranch = false, parentId = null) => {
    if (typeof startNodeId == 'string') {
        startNodeId = parseInt(startNodeId);
    }
    let subNodes = [];
    let flowNodes = Object.keys(nodes ?? {}).map((key) => {
        const node = nodes[key];
        let branchNodes = null;
        if (showBranch && node.type === 'branch' && node?.asset_title && !parentId) {
            branchNodes = generateProcedureFlow(
                Number(node.branch_procedure?.start_node_id ?? 1),
                node?.branch_procedure?.nodes ?? [],
                treeRanker,
                showBranch,
                node.id
            );
            if (branchNodes?.nodes?.length > 0) {
                subNodes.push(...branchNodes.nodes);
            }
        }

        return {
            title: node.title,
            id: parentId ? parentId + ':' + node.id : node.id,
            icons: [node?.type].map(
                (type) =>
                    NodeTypeIcons?.[type] && (
                        <Tooltip key={type} title={NodeTypeIcons[type].title}>
                            {NodeTypeIcons[type].icon}
                        </Tooltip>
                    )
            ),
            isHome: String(node.id) === String(startNodeId),
            connections: getNodeConnections(node, nodes, parentId),
            inConnections: [],
            order_idx: node?.order_idx,
            data: node,
            parentId,
            branchNode: branchNodes ?? null,
        };
    });

    if (subNodes?.length > 0) {
        flowNodes = [...flowNodes, ...subNodes];
    }
    return {
        nodeWidth: 200,
        nodeHeight: 100,
        marginTop: 20,
        marginLeft: 20,
        nodeHorizontalSpacing: 40,
        nodeVerticalSpacing: 60,
        activeNode: startNodeId,
        connectors: [],
        ranker: ranker,
        nodes: flowNodes,
    };
};

const NodeManagerDisplay = ({
    showNodeFlow,
    flowData,
    selectedNodeId,
    showTwoPanelMode,
    handleNodeSelected,
    procedure,
    sortedNodes,
    handleNodeClick,
    handleNodePreview,
    handleNodesChanged,
    handleNodeDuplicate,
    showDeleteNodeDialog,
    setClickedAddButton,
    compactMode,
    showBranches,
    setOpenSaveDataDialog,
    setComponentMethodsRef,
    setOpenFlowSettingsDialog,
    nodeDisplayPanel,
    isCreatingNode,
}) => {
    return showNodeFlow ? (
        <ProcedureFlow
            setComponentMethodsRef={setComponentMethodsRef}
            draggable={false}
            layoutEngine={layoutEngines.dagreLayout}
            data={flowData}
            activeNodeId={selectedNodeId}
            showTwoPanelMode={showTwoPanelMode}
            onNodeClicked={handleNodeClick}
            onNodeActive={handleNodeSelected}
            onNodeDelete={showDeleteNodeDialog}
            onNodeDuplicate={handleNodeDuplicate}
            onNodePreview={(node) => handleNodePreview({ procedure, node })}
            setClickedAddButton={setClickedAddButton}
            openSettings={setOpenFlowSettingsDialog}
            nodeDisplayPanel={nodeDisplayPanel}
            isCreatingNode={isCreatingNode}
        />
    ) : (
        <NodeTable
            procedure={procedure}
            nodes={sortedNodes}
            activeNodeId={selectedNodeId}
            onNodeClicked={handleNodeClick}
            onNodeActive={handleNodeSelected}
            onNodePreview={handleNodePreview}
            onNodesChanged={handleNodesChanged}
            onNodeDuplicate={handleNodeDuplicate}
            onNodeDelete={showDeleteNodeDialog}
            setClickedAddButton={setClickedAddButton}
            compactMode={compactMode}
            showBranches={showBranches}
            showTwoPanelMode={showTwoPanelMode}
            setOpenSaveDataDialog={setOpenSaveDataDialog}
            isCreatingNode={isCreatingNode}
        />
    );
};

const ProcedureDesigner = ({ procedureId, procedure, parentWidth, parentHeight, assetId, fetchAsset }) => {
    //#region Constants
    const dispatch = useDispatch();
    const { enqueueSnackbar } = useSnackbar();
    const procedureStyles = makeProcedureStyles();
    const resizableStyles = makeResizableStyles();
    //#endregion Constants

    //#region Hooks
    //#endregion Hooks

    //#region State
    // eslint-disable-next-line no-unused-vars
    const [componentMethodsRef, setComponentMethodsRef] = useState({});
    const [activeNode, setActiveNode] = useState(null);
    const [previewMode, setPreviewMode] = useState(false);
    const [sortedNodes, setSortedNodes] = useState(sortNodes(procedure.nodes));
    const [selectedNodeId, setSelectedNodeId] = useState(1);
    const [previewWidth, setPreviewWidth] = useState(parentWidth);
    const [phoneWidth, setPhoneWidth] = useState(false);
    const [showEditDialog, setShowEditDialog] = useState(false);
    const [variables, setVariables] = useState([]);
    const [openFlowSettingsDialog, setOpenFlowSettingsDialog] = useState(false);
    const [openSaveDataDialog, setOpenSaveDataDialog] = useState(false);
    const [clickedAddButton, setClickedAddButton] = useState(false);
    const [compactMode, setCompactMode] = useState(true);
    const [showBranches, setShowBranches] = useState(false);
    const [showNodeFlow, setShowNodeFlow] = useState(true);
    const [showTwoPanelMode, setShowTwoPanelMode] = useState(true);
    const [openDeleteConfirmDialog, setOpenDeleteConfirmDialog] = useState(false);
    const [selectedDeleteNode, setSelectedDeleteNode] = useState(null);
    const [openNodeEditForm, setOpenNodeEditForm] = useState(false);
    const [openCategoryDialog, setOpenCategoryDialog] = useState(false);
    const [openInsertWarningDialog, setOpenInsertWarningDialog] = useState(false);
    const [pendingInsertIdx, setPendingInsertIdx] = useState(false);
    const [pendingNodeType, setPendingNodeType] = useState(null);
    const [pendingDuplicateIds, setPendingDuplicateIds] = useState(null);
    const [openDuplicateDialog, setOpenDuplicateDialog] = useState(false);
    // Tracks if the pending insert came from tree-view (needs editor dialog) or list-view.
    const [pendingFromTreeView, setPendingFromTreeView] = useState(false);
    // Insert index stored when + is clicked in flow canvas; cleared after editor opens.
    const [treeViewPendingInsertIdx, setTreeViewPendingInsertIdx] = useState(null);
    const [openTreeViewNodeTypeDialog, setOpenTreeViewNodeTypeDialog] = useState(false);
    const [openTreeViewNodeEditDialog, setOpenTreeViewNodeEditDialog] = useState(false);
    const [flowData, setFlowData] = useState(
        generateProcedureFlow(Number(procedure.start_node_id), sortedNodes, treeRanker)
    );
    //#endregion State

    //#region Selectors
    const unsavedProcedure = useSelector(getProcedureUnsavedData);
    const isProcedureDirty = useSelector(getProcedureDirty);
    const currentNode = useSelector(getCurrentNode);
    const createNodeData = useSelector(getProcedureCreateNodeData);
    const activeUser = useSelector(accountsSlice.selectActiveUser);
    const readOnlyMode = activeUser?.role === Role.User ? true : false;
    //#endregion Selectors

    //#region Refs
    const nodeDisplayPanel = useRef(null);
    const nodePreviewPanel = useRef(null);
    // Saves the active node before create mode starts, restored on cancel.
    const previousActiveNodeRef = useRef(null);
    // Snapshots of sortedNodes and flowData taken just before the draft preview is inserted.
    // Used to restore the exact pre-add state on cancel.
    const snapshotSortedNodesRef = useRef(null);
    const snapshotFlowDataRef = useRef(null);
    //#endregion Refs

    // Derived: true while a brand-new draft node is being edited in the right panel (two-panel mode).
    // Never gets stuck because it is computed from local state, not stored separately.
    const isCreatingNode = showTwoPanelMode && !!activeNode?.isNew;

    //#region Effects
    useEffect(() => {
        if (unsavedProcedure) {
            setFlowData(
                generateProcedureFlow(Number(unsavedProcedure?.start_node_id), sortedNodes, treeRanker, showBranches)
            );
        }
    }, [showBranches, unsavedProcedure, sortedNodes]);

    useEffect(() => {
        async function createNodeForBtnTarget() {
            if (
                createNodeData &&
                createNodeData?.currentNode &&
                createNodeData?.newNodeType &&
                Array.isArray(createNodeData?.currentNode?.option_buttons) &&
                createNodeData?.currentNode?.option_buttons?.length > 0
            ) {
                let newNode = await handleAddNode(createNodeData?.newNodeType, false, false, false);
                let btnOptions = createNodeData?.currentNode?.option_buttons?.map((btn) => {
                    if (btn?.action?.id === 'new-node') {
                        btn = {
                            ...btn,
                            action: {
                                ...btn?.action,
                                id: newNode.id,
                            },
                        };
                        newNode = { ...newNode, title: btn?.button_text };
                    }
                    return btn;
                });
                let btnNode = {
                    ...createNodeData?.currentNode,
                    option_buttons: btnOptions,
                };
                await handleSubmit(newNode, btnNode);
            }
        }
        createNodeForBtnTarget();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [createNodeData]);

    useEffect(() => {
        dispatch(setActiveAssetId(assetId));
        dispatch(setActiveAssetProcedureId(procedureId));
        dispatch(setChecklistInitialState());
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [assetId, procedureId]);

    useEffect(() => {
        if (procedure) {
            changeProcedure(procedure, true);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [procedureId]);

    useEffect(() => {
        if (unsavedProcedure) {
            changeProcedure(unsavedProcedure, false);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [unsavedProcedure]);

    useEffect(() => {
        setPreviewWidth(parentWidth);
    }, [parentWidth]);
    //#endregion Effects

    //#region Methods
    const focusTitleField = useCallback((attempt = 0) => {
        const titleField = document.querySelector('input[name="title"]');
        if (titleField) {
            titleField.focus();
            titleField.select?.();
            return;
        }

        if (attempt < 6) {
            setTimeout(() => {
                focusTitleField(attempt + 1);
            }, 50);
        }
    }, []);

    const notifyTitleRequiredAndFocusNode = useCallback(
        (node) => {
            enqueueSnackbar('Node title is required before creating or duplicating a node.', {
                action: (key) => <SnackbarDismiss key={key} />,
                variant: 'error',
            });

            if (node) {
                setSelectedNodeId(node.id);
                setActiveNode(node);
                setOpenNodeEditForm(true);
                emitNodeInitialView(node);
            }

            focusTitleField();
        },
        [enqueueSnackbar, focusTitleField]
    );

    const handleSetClickedAddButton = useCallback(
        (value) => {
            if (value === false) {
                setClickedAddButton(false);
                return;
            }

            if (typeof value === 'number' && value > 0) {
                const sourceNode = sortedNodes[value - 1];
                if (sourceNode && !String(sourceNode?.title ?? '').trim()) {
                    notifyTitleRequiredAndFocusNode(sourceNode);
                    setClickedAddButton(false);
                    return;
                }
            }

            // In tree/flow view, intercept add to open the type picker before inserting.
            if (showNodeFlow) {
                // When in single-panel mode, there is no right panel for create mode.
                // Fall through to the list-view add path so Instance B handles it.
                if (!showTwoPanelMode) {
                    setClickedAddButton(value);
                    return;
                }
                if (isProcedureDirty) {
                    setOpenSaveDataDialog(true);
                    return;
                }
                setTreeViewPendingInsertIdx(value);
                setOpenTreeViewNodeTypeDialog(true);
                return;
            }

            setClickedAddButton(value);
        },
        [notifyTitleRequiredAndFocusNode, sortedNodes, showNodeFlow, showTwoPanelMode, isProcedureDirty]
    );

    // Opens the node editor in the right panel for a new node of the given type,
    // without saving first. Used by the flow canvas "+" path (two-panel mode only).
    const emitNodeInitialView = useCallback(
        (node, procedureState = unsavedProcedure ?? procedure) => {
            if (!node) return;
            try {
                dispatch(setProcedureState(procedureState));
                dispatch(setChecklistInitialState());
                dispatch(gotoNode(node.id));
            } catch (err) {
                // swallow errors to avoid breaking UI if dispatch isn't available for any reason
            }
        },
        [dispatch, unsavedProcedure, procedure]
    );

    const openTreeViewEditorForType = useCallback(
        async (nodeType, afterOrderIdx) => {
            dispatch(activateLoading());
            const updatedProcedure = await dispatch(
                fetchProcedure({ service: procedureService, procedureId: procedureId })
            );
            dispatch(deactivateLoading());

            if (updatedProcedure.error) {
                enqueueSnackbar(updatedProcedure.error, {
                    action: (key) => <SnackbarDismiss key={key} />,
                    variant: 'error',
                });
                // Do NOT enter create mode — server fetch failed.
                return;
            }

            const updatedProcedureJson = updatedProcedure.payload.procedure;
            const updatedSortedNodes = sortNodes(updatedProcedureJson.nodes);
            const lastNode = last(updatedSortedNodes) ?? { id: 0, order_idx: -1 };

            // When inserting between existing nodes, default next/back buttons to true so
            // the new node slots naturally into the sequence.
            // Content nodes always default next/back to true regardless of position —
            // matching the behaviour of duplicating a node in the tree view.
            const isInsertBetween = Boolean(afterOrderIdx);
            const isContentNode = nodeType === ProcedureNodeTypes.content.type;

            const newNode =
                nodeType === ProcedureNodeTypes.branch.type
                    ? {
                          id: getNewNodeId(updatedSortedNodes),
                          type: nodeType,
                          order_idx: lastNode.order_idx + 1,
                          title: '',
                          isNew: true,
                          ...(afterOrderIdx ? { afterOrderIdx } : {}),
                      }
                    : {
                          id: getNewNodeId(updatedSortedNodes),
                          type: nodeType,
                          order_idx: lastNode.order_idx + 1,
                          back_button: isInsertBetween || isContentNode,
                          next_button: isInsertBetween || isContentNode,
                          submit_button: false,
                          option_buttons: [],
                          title: '',
                          question: '',
                          content: '',
                          fields: [],
                          target_title: '',
                          target_details: '',
                          target_url: '',
                          isNew: true,
                          ...(afterOrderIdx ? { afterOrderIdx } : {}),
                          // Logic node targets — initialised so selects are controlled from the start
                          ...(nodeType === ProcedureNodeTypes.logic.type
                              ? { pass_target_node_id: null, fail_target_node_id: null }
                              : {}),
                      };

            // Build preview list with draft at the correct position.
            let previewSortedNodes;
            if (afterOrderIdx) {
                const insertAt = Math.min(Math.max(0, afterOrderIdx), updatedSortedNodes.length);
                const previewNodes = [...updatedSortedNodes];
                previewNodes.splice(insertAt, 0, { ...newNode });
                previewSortedNodes = previewNodes.map((nd, idx) => ({ ...nd, order_idx: idx }));
            } else {
                previewSortedNodes = [...updatedSortedNodes, { ...newNode }];
            }
            const previewFlowData = generateProcedureFlow(Number(procedure?.start_node_id), previewSortedNodes, treeRanker);

            // Snapshot the pre-add state so cancel can restore it exactly.
            snapshotSortedNodesRef.current = updatedSortedNodes;
            snapshotFlowDataRef.current = generateProcedureFlow(Number(procedure?.start_node_id), updatedSortedNodes, treeRanker);

            // Save the previously selected node for restore on cancel.
            previousActiveNodeRef.current = activeNode;

            setSelectedNodeId(newNode.id);
            setActiveNode(newNode);
            setSortedNodes(previewSortedNodes);
            setFlowData(previewFlowData);
            setTreeViewPendingInsertIdx(null);

            // Lock the UI immediately — DirtyProcedure's isNewNode guard prevents it being
            // overwritten back to false by Formik's initial dirty=false state.
            dispatch(setProcedureDirty(true));

            // Focus the title field in the right panel.
            focusTitleField();
            emitNodeInitialView(newNode);
        },
        [dispatch, procedureId, procedure, enqueueSnackbar, activeNode, focusTitleField, emitNodeInitialView]
    );

    // Handles node type selection from the tree-view type picker dialog.
    const handleTreeViewNodeTypeSelected = useCallback(
        async (nodeType) => {
            setOpenTreeViewNodeTypeDialog(false);

            const sourceNode =
                treeViewPendingInsertIdx !== null && treeViewPendingInsertIdx > 0
                    ? sortedNodes[treeViewPendingInsertIdx - 1]
                    : null;
            const requiresConfirm = isFlowImpacting(nodeType) || isFlowImpacting(sourceNode?.type);

            if (requiresConfirm) {
                // Show warning dialog; on confirm proceed via tree-view path.
                setPendingNodeType(nodeType);
                setPendingInsertIdx(treeViewPendingInsertIdx);
                setPendingFromTreeView(true);
                setOpenInsertWarningDialog(true);
                return;
            }

            await openTreeViewEditorForType(nodeType, treeViewPendingInsertIdx);
        },
        [treeViewPendingInsertIdx, sortedNodes, openTreeViewEditorForType]
    );

    // Cancels create mode in the right panel.
    // Removes the draft preview, restores the exact pre-add state, and unlocks the UI.
    const handleCreateModeCancel = useCallback(() => {
        // Restore the exact snapshot taken before the preview was inserted.
        const restoredNodes = snapshotSortedNodesRef.current;
        const restoredFlowData = snapshotFlowDataRef.current;

        if (restoredNodes !== null) {
            setSortedNodes(restoredNodes);
        }
        if (restoredFlowData !== null) {
            setFlowData(restoredFlowData);
        }

        // Restore the previously selected node by looking it up in the restored list,
        // so we always use the server-saved values, not a potentially stale ref object.
        const prev = previousActiveNodeRef.current;
        if (prev) {
            const freshPrev = restoredNodes ? restoredNodes.find((nd) => String(nd?.id) === String(prev.id)) ?? prev : prev;
            setActiveNode(freshPrev);
            setSelectedNodeId(freshPrev.id);
            emitNodeInitialView(freshPrev);
        } else {
            setActiveNode(null);
            setSelectedNodeId(null);
        }

        // Clear refs.
        previousActiveNodeRef.current = null;
        snapshotSortedNodesRef.current = null;
        snapshotFlowDataRef.current = null;

        // Unlock the UI. Also reset original procedure data so nothing from the
        // draft leaks into unsavedData.
        dispatch(setOriginalProcedureData(procedure));
        dispatch(setProcedureDirty(false));
    }, [dispatch, procedure, emitNodeInitialView]);

    async function changeProcedure(proc, callFromOriginal = true) {
        let currentProcedure = proc;
        const updatedSortedNodes = sortNodes([...(proc?.nodes ?? [])]);

        if (callFromOriginal === true) {
            const repairedStartNodeId = getValidatedStartNodeId(
                proc?.start_node_id,
                updatedSortedNodes
            );

            const startNodeNeedsRepair =
                repairedStartNodeId === null
                    ? proc?.start_node_id !== null
                    : String(repairedStartNodeId) !== String(proc?.start_node_id);

            if (startNodeNeedsRepair) {
                const repairedProcedure = {
                    ...proc,
                    start_node_id: repairedStartNodeId,
                };

                const result = await dispatch(
                    updateProcedure({
                        header_image_url: proc?.header_image_url,
                        procedure_id: procedureId,
                        procedure_json: JSON.stringify(repairedProcedure),
                        asset_id: assetId,
                    })
                );

                if (!result.error) {
                    currentProcedure = repairedProcedure;
                }
            }
        }

        const currentSortedNodes = sortNodes([
            ...(currentProcedure?.nodes ?? []),
        ]);

        setVariables(getAllProcedureVariables(currentSortedNodes));
        setSortedNodes(currentSortedNodes);

        setFlowData(
            generateProcedureFlow(
                getEffectiveStartNodeId(currentProcedure),
                currentSortedNodes,
                treeRanker
            )
        );

        if (callFromOriginal === true) {
            let homeNodeId = getEffectiveStartNodeId(currentProcedure);

            if (activeNode?.id) {
                homeNodeId = activeNode.id;
            }

            if (homeNodeId !== null) {
                setSelectedNodeId(homeNodeId);
            }

            setActiveNode(
                currentSortedNodes.find(
                    (nd) => String(nd?.id) === String(homeNodeId)
                ) ?? null
            );

            dispatch(setOriginalProcedureData(currentProcedure));
        }

        if (createNodeData?.currentNode) {
            setActiveNode(createNodeData.currentNode);
            setSelectedNodeId(createNodeData.currentNode.id);
            dispatch(setProcedureCreateNodeData(null));
        }
    }

    const getNewNodeId = (nodes) => {
        const usedIds = nodes.map((n) => Number(n.id));
        return usedIds.length === 0 ? 1 : Math.max(...usedIds) + 1;
    };

    const showDeleteNodeDialog = useCallback((nodeId) => {
        if (isProcedureDirty === true) {
            setOpenSaveDataDialog(true);
            return;
        }
        setSelectedDeleteNode(nodeId);
        setOpenDeleteConfirmDialog(true);
    }, [isProcedureDirty]);

    const closeDeleteConfirmDialog = () => {
        setSelectedDeleteNode(null);
        setOpenDeleteConfirmDialog(false);
    };

    // Wrap handleAddNode: show confirmation when the new node type or the source node is flow-impacting.
    const handleAddNodeWithConfirm = (nodeType, afterOrderIdx = false, makeActive = true, checkDirty = true) => {
        // Dirty check first — same guard that handleAddNode itself applies.
        if (isProcedureDirty === true && checkDirty) {
            setOpenSaveDataDialog(true);
            return;
        }

        const sourceNode = (afterOrderIdx !== false && afterOrderIdx > 0) ? sortedNodes[afterOrderIdx - 1] : null;
        const requiresConfirm = isFlowImpacting(nodeType) || isFlowImpacting(sourceNode?.type);

        if (requiresConfirm) {
            setPendingNodeType(nodeType);
            setPendingInsertIdx(afterOrderIdx);
            setOpenInsertWarningDialog(true);
        } else {
            handleAddNode(nodeType, afterOrderIdx, makeActive, checkDirty);
        }
    };

    const confirmInsert = () => {
        setOpenInsertWarningDialog(false);
        if (pendingFromTreeView) {
            // Tree-view path: open editor dialog so the author configures before saving.
            const nodeType = pendingNodeType;
            const insertIdx = pendingInsertIdx;
            setPendingNodeType(null);
            setPendingInsertIdx(false);
            setPendingFromTreeView(false);
            openTreeViewEditorForType(nodeType, insertIdx);
        } else {
            // Pass checkDirty=false — dirty guard already ran in handleAddNodeWithConfirm.
            handleAddNode(pendingNodeType, pendingInsertIdx, true, false);
            setPendingNodeType(null);
            setPendingInsertIdx(false);
        }
    };

    const cancelInsert = () => {
        setOpenInsertWarningDialog(false);
        setPendingNodeType(null);
        setPendingInsertIdx(false);
        setPendingFromTreeView(false);
        setTreeViewPendingInsertIdx(null);
    };

    const handleNodeDelete = async () => {
        let filteredNodes = [];
        if (Array.isArray(selectedDeleteNode)) {
            filteredNodes = await sortedNodes.filter((nd) => selectedDeleteNode.includes(nd.id) === false);
        } else {
            filteredNodes = await sortedNodes.filter((nd) => nd.id !== selectedDeleteNode);
        }
        setSortedNodes(filteredNodes);
        handleNodesChanged(filteredNodes, false);
        closeDeleteConfirmDialog();
    };

    const handleNodeClick = useCallback(
        (node) => {
            if (isProcedureDirty === true) {
                setOpenSaveDataDialog(true);
                return;
            }
            setSelectedNodeId(node.id);
            setActiveNode(node);
            setOpenNodeEditForm(true);
            emitNodeInitialView(node);
        },
        [dispatch, isProcedureDirty, procedure]
    );

    const handleNodeSelected = useCallback(
        (nodeId, node) => {
            if (isProcedureDirty === true) {
                setOpenSaveDataDialog(true);
                return;
            }
            if (showTwoPanelMode === true) {
                handleNodeClick(node);
            } else {
                setSelectedNodeId(nodeId);
                setActiveNode(node);
                setOpenNodeEditForm(true);
                emitNodeInitialView(node);
            }
        },
        [isProcedureDirty, showTwoPanelMode, handleNodeClick]
    );

    useEffect(() => {
        if (showTwoPanelMode && !activeNode && selectedNodeId) {
            const node = procedure?.nodes?.find((x) => x.id === selectedNodeId);
            if (node) {
                setActiveNode(node);
            }
        }
    }, [showTwoPanelMode]);

    const handleAddNode = async (nodeType, afterOrderIdx = false, makeActive = true, checkDirty = true) => {
        if (afterOrderIdx !== false && afterOrderIdx > 0) {
            const sourceNode = sortedNodes[afterOrderIdx - 1];
            if (sourceNode && !String(sourceNode?.title ?? '').trim()) {
                notifyTitleRequiredAndFocusNode(sourceNode);
                return;
            }
        }

        // Make sure we have the latest nodes from the server
        if (isProcedureDirty === true && checkDirty) {
            setOpenSaveDataDialog(true);
            return;
        }
        dispatch(activateLoading());
        const updatedProcedure = await dispatch(
            fetchProcedure({
                service: procedureService,
                procedureId: procedureId,
            })
        );
        dispatch(deactivateLoading());
        if (updatedProcedure.error) {
            enqueueSnackbar(updatedProcedure.error, {
                action: (key) => <SnackbarDismiss key={key} />,
                variant: 'error',
            });
            return;
        }
        const updatedProcedureJson = updatedProcedure.payload.procedure;

        const updatedSortedNodes = sortNodes(updatedProcedureJson.nodes);

        let lastNode = last(updatedSortedNodes);
        if (!lastNode) {
            lastNode = { id: 0, order_idx: -1 };
        }
        let newNode =
            nodeType === ProcedureNodeTypes.branch.type
                ? {
                      id: getNewNodeId(updatedSortedNodes),
                      type: nodeType,
                      order_idx: lastNode.order_idx + 1,
                      title: '',
                  }
                : {
                      id: getNewNodeId(updatedSortedNodes),
                      type: nodeType,
                      order_idx: lastNode.order_idx + 1,
                      // When inserting between existing nodes (afterOrderIdx is set), default both
                      // next/back to true. Content nodes always default both to true regardless
                      // of position — matching the behaviour of duplicating a node in the tree view.
                      back_button: Boolean(afterOrderIdx) || nodeType === ProcedureNodeTypes.content.type,
                      next_button: Boolean(afterOrderIdx) || nodeType === ProcedureNodeTypes.content.type,
                      submit_button: false,
                      option_buttons: [],
                      title: '',
                      question: '',
                      content: '',
                      fields: [],
                      target_title: '',
                      target_details: '',
                      target_url: '',
                      action: { id: '', type: 'node' },
                      // Logic node targets — initialised so selects are controlled from the start
                      ...(nodeType === ProcedureNodeTypes.logic.type
                          ? { pass_target_node_id: null, fail_target_node_id: null }
                          : {}),
                  };
        newNode = { ...newNode, isNew: true };
        if (afterOrderIdx) {
            newNode = { ...newNode, afterOrderIdx };
        }
        if (makeActive) {
            setSelectedNodeId(newNode.id);
            setActiveNode(newNode);
            setOpenNodeEditForm(true);

            // Build preview list with new node at the correct position, then sync both state and flow.
            let previewSortedNodes;
            if (afterOrderIdx) {
                const insertAt = Math.min(Math.max(0, afterOrderIdx), updatedSortedNodes.length);
                const previewNodes = [...updatedSortedNodes];
                previewNodes.splice(insertAt, 0, { ...newNode });
                previewSortedNodes = previewNodes.map((nd, idx) => ({ ...nd, order_idx: idx }));
            } else {
                previewSortedNodes = [...updatedSortedNodes, { ...newNode }];
            }

            setSortedNodes(previewSortedNodes);
            setFlowData(generateProcedureFlow(Number(procedure?.start_node_id), previewSortedNodes, treeRanker));

            // In two-panel mode enter create mode: snapshot pre-add state, save previous node,
            // and force dirty so the UI locks until the author submits or cancels.
            if (showTwoPanelMode) {
                snapshotSortedNodesRef.current = updatedSortedNodes;
                snapshotFlowDataRef.current = generateProcedureFlow(Number(procedure?.start_node_id), updatedSortedNodes, treeRanker);
                previousActiveNodeRef.current = activeNode;
                dispatch(setProcedureDirty(true));
                focusTitleField();
                emitNodeInitialView(newNode);
            }
        }
        return newNode;
    };

    const handleSubmit = useCallback(
        async (values, currNode = null, nodes) => {
            let node = {};
            let procedureCategories = [];
            node = { ...values };

            if (values?.procedureCategories) {
                procedureCategories = values.procedureCategories;
                delete node.procedureCategories;
            }
            delete node?.isNew;
            delete node?.focusTargetBtnId;
            dispatch(activateLoading({ showProgress: true }));
            let newSortedNodes = [];
            const nodeIdx = sortedNodes.findIndex((anode) => anode.id === node.id);
            let updatedProcedure = { ...(unsavedProcedure ?? procedure) };
            if (procedureCategories.length > 0) {
                updatedProcedure.procedureCategories = [...procedureCategories];
            }
            // Priority 1: positional insert — check afterOrderIdx before findIndex.
            if (node?.afterOrderIdx !== undefined && node?.afterOrderIdx !== false && node?.afterOrderIdx !== null) {
                // Remove the preview copy (added by handleAddNode) before splicing the final version.
                const baseNodes = sortedNodes.filter((nd) => nd.id !== node.id);

                const insertAt = Math.min(Math.max(0, node.afterOrderIdx), baseNodes.length);
                const spliced = [...baseNodes];
                spliced.splice(insertAt, 0, { ...node });

                // Re-index all nodes to contiguous 0..N.
                newSortedNodes = spliced.map((nd, idx) => {
                    const cleaned = { ...nd };
                    delete cleaned.afterOrderIdx;
                    cleaned.order_idx = idx;
                    return cleaned;
                });
            } else if (nodeIdx !== -1) {
                newSortedNodes = sortedNodes.map((nd, idx) => (idx === nodeIdx ? node : nd));
            } else {
                const cleanNode = { ...node };
                delete cleanNode.afterOrderIdx;
                newSortedNodes = [...sortedNodes, cleanNode];
            }

            updatedProcedure.start_node_id = getValidatedStartNodeId(
                updatedProcedure.start_node_id,
                newSortedNodes
            );

            let procedureWithoutBranch = cloneDeep(updatedProcedure);

            removeBranchProcedure(procedureWithoutBranch.nodes);
            removeBranchProcedure(newSortedNodes);

            const result = await dispatch(
                updateProcedure({
                    header_image_url: procedure?.header_image_url,
                    procedure_id: procedureId,
                    procedure_json: JSON.stringify(
                        await setHeaderVariables({
                            ...procedureWithoutBranch,
                            ...{ nodes: newSortedNodes },
                        })
                    ),
                    asset_id: assetId,
                })
            );
            if (result.error) {
                dispatch(deactivateLoading());
                enqueueSnackbar(result.error.message, {
                    action: (key) => <SnackbarDismiss key={key} />,
                    variant: 'error',
                });
                return;
            }
            const updatedProcedureJson = result.payload.procedure;

            const updatedSortedNodes = sortNodes(updatedProcedureJson.nodes);

            if (currNode && Array.isArray(updatedSortedNodes) && updatedSortedNodes?.length > 0) {
                if (currNode?.isNew === true) {
                    const lastNode = last(updatedSortedNodes);
                    currNode = {
                        ...currNode,
                        id: getNewNodeId(updatedSortedNodes),
                        order_idx: lastNode?.order_idx + 1,
                    };
                }
                dispatch(setProcedureCreateNodeData({ currentNode: currNode }));
            }
            setSortedNodes(updatedSortedNodes);
            setFlowData(generateProcedureFlow(getEffectiveStartNodeId(updatedProcedureJson), updatedSortedNodes, treeRanker));
            setSelectedNodeId(node.id);

            // Set active node from server response to get the correct order_idx and no afterOrderIdx.
            const savedNode = updatedSortedNodes.find((nd) => String(nd.id) === String(node.id)) ?? node;
            setActiveNode(savedNode);
            dispatch(setOriginalProcedureData(updatedProcedureJson));
            if (node.type === 'branch') {
                fetchAsset();
            }
            dispatch(setProcedureDirty(false));
            // Clear create-mode refs on successful save.
            previousActiveNodeRef.current = null;
            snapshotSortedNodesRef.current = null;
            snapshotFlowDataRef.current = null;
            setOpenNodeEditForm(false);
            dispatch(deactivateLoading());
        },
        [dispatch, sortedNodes, procedure, unsavedProcedure, procedureId, assetId, enqueueSnackbar, fetchAsset]
    );

    const removeBranchProcedure = (nodes) => {
        for (let key in nodes) {
            if (Object.prototype.hasOwnProperty.call(nodes, key)) {
                const node = nodes[key];
                if (node.type === 'branch') {
                    delete node.branch_procedure;
                }
            }
        }
    };

    const handleNodeDuplicate = useCallback((nodeId) => {
        if (isProcedureDirty === true) {
            setOpenSaveDataDialog(true);
            return;
        }
        const nodeIds = Array.isArray(nodeId) ? nodeId : [nodeId];

        const selectedNodeWithMissingTitle = nodeIds
            .map((id) => sortedNodes.find((n) => n?.id === id))
            .find((node) => node && !hasNodeTitle(node));

        if (selectedNodeWithMissingTitle) {
            notifyTitleRequiredAndFocusNode(selectedNodeWithMissingTitle);
            return;
        }

        // Store the node IDs and open the duplicate dialog
        setPendingDuplicateIds(nodeIds);
        setOpenDuplicateDialog(true);
    }, [isProcedureDirty, sortedNodes, notifyTitleRequiredAndFocusNode]);

    const handleDuplicateConfirm = (count) => {
        if (!pendingDuplicateIds) return;
        // Execute duplicate directly — no flow-impact warning needed for duplicates.
        // The copied node is inserted adjacent to its source and does not break existing connections.
        executeDuplicate(pendingDuplicateIds, count);
    };

    const executeDuplicate = async (nodeIds, duplicateCount = 1) => {
        dispatch(activateLoading({ showProgress: true }));
        const variableNames = variables.map((v) => v.name);
        const untitledNodes = sortedNodes.filter((node) => !hasNodeTitle(node) && !nodeIds.includes(node?.id));
        const untitledNodeIds = untitledNodes.map((node) => node.id);
        let recursiveSortNodes = sortedNodes
            .filter((node) => hasNodeTitle(node) || nodeIds.includes(node?.id))
            .map((node, idx) => ({ ...node, order_idx: idx }));
        let newNodesAdded = [];
        let newNodesTypes = [];

        const duplicateCandidateIds = nodeIds.filter((id) => recursiveSortNodes.some((node) => node?.id === id));

        // Process in descending order so earlier splices don't shift later source indices.
        const sortedIds = [...duplicateCandidateIds].sort((a, b) => {
            const nodeA = recursiveSortNodes.find((n) => n?.id === a);
            const nodeB = recursiveSortNodes.find((n) => n?.id === b);
            return (nodeB?.order_idx ?? 0) - (nodeA?.order_idx ?? 0);
        });

        for (const id of sortedIds) {
            const sourceNode = recursiveSortNodes?.find((node) => node?.id === id);
            if (!sourceNode) continue;

            // Create the specified number of duplicates for each node
            for (let i = 0; i < duplicateCount; i++) {
                let copyNode = {
                    ...sourceNode,
                    id: getNewNodeId(recursiveSortNodes),
                    title: `${sourceNode.title} copy${duplicateCount > 1 ? ` ${i + 1}` : ''}`,
                };
                delete copyNode.initial_view;
                if (copyNode.variable) {
                    copyNode.variable = getUniqueVariableName(copyNode.variable, variableNames);
                }
                if (copyNode.type === ProcedureNodeTypes.content.type && Array.isArray(copyNode.fields)) {
                    // Content: re-map fields with unique IDs and variable names so variables don't collide
                    copyNode.fields = copyNode.fields.map((field) => copyField(field, variableNames));
                } else if (copyNode.type === ProcedureNodeTypes.logic.type) {
                    // Logic: deep-copy logic_rules to avoid shared object references
                    if (copyNode.logic_rules) {
                        copyNode.logic_rules = JSON.parse(JSON.stringify(copyNode.logic_rules));
                    }
                    // Preserve pass_target_node_id / fail_target_node_id — valid targets to carry over
                    if (copyNode.pass_target_node_id) {
                        copyNode.pass_target_node_id = { ...copyNode.pass_target_node_id };
                    }
                    if (copyNode.fail_target_node_id) {
                        copyNode.fail_target_node_id = { ...copyNode.fail_target_node_id };
                    }
                } else if (copyNode.type === ProcedureNodeTypes.branch.type) {
                    // duplicate points to the same branch procedure as its source.
                    delete copyNode.branch_procedure;
                } else if (
                    copyNode.type === ProcedureNodeTypes.navigationAction.type ||
                    copyNode.type === ProcedureNodeTypes.contentViewAction.type ||
                    copyNode.type === ProcedureNodeTypes.email.type
                ) {
                    // Action nodes: shallow-copy the action object so it isn't shared
                    if (copyNode.action) {
                        copyNode.action = { ...copyNode.action };
                    }
                    // Deep-copy emails array for email nodes
                    if (copyNode.type === ProcedureNodeTypes.email.type && Array.isArray(copyNode.emails)) {
                        copyNode.emails = [...copyNode.emails];
                    }
                }

                // Deep-copy option_buttons so button objects aren't shared across duplicates
                if (Array.isArray(copyNode.option_buttons)) {
                    copyNode.option_buttons = copyNode.option_buttons.map((btn) => ({ ...btn }));
                }

                // Splice copy immediately after source, then re-index to 0..N.
                const sourceIdx = recursiveSortNodes.findIndex((n) => n?.id === sourceNode.id);
                const insertAt = sourceIdx !== -1 ? sourceIdx + 1 : recursiveSortNodes.length;
                const spliced = [...recursiveSortNodes];
                spliced.splice(insertAt, 0, copyNode);
                recursiveSortNodes = spliced.map((n, idx) => ({ ...n, order_idx: idx }));

                newNodesAdded.push(copyNode.id);
                newNodesTypes.push(copyNode.type);
            }
        }

        const hasStructureChanges = untitledNodeIds.length > 0 || newNodesAdded.length > 0;
        if (!hasStructureChanges) {
            dispatch(deactivateLoading());
            return;
        }

        // Reverse to show snackbar IDs in ascending order.
        newNodesAdded.reverse();

        const currentProcedure = unsavedProcedure ?? procedure;

        const updatedProcedure = {
            ...currentProcedure,
            nodes: [...recursiveSortNodes],
            start_node_id: getValidatedStartNodeId(
                currentProcedure?.start_node_id,
                recursiveSortNodes
            ),
        };            

        const result = await dispatch(
            updateProcedure({
                header_image_url: currentProcedure?.header_image_url,
                procedure_id: procedureId,
                procedure_json: JSON.stringify(
                    await setHeaderVariables(updatedProcedure)
                ),
                asset_id: assetId,
            })
        );

        if (result.error) {
            dispatch(deactivateLoading());
            enqueueSnackbar(result.error, {
                action: (key) => <SnackbarDismiss key={key} />,
                variant: 'error',
            });
            return;
        } else {
            if (untitledNodeIds.length > 0) {
                enqueueSnackbar(`Removed untitled node #${untitledNodeIds.join(', #')}.`, {
                    action: (key) => <SnackbarDismiss key={key} />,
                    variant: 'warning',
                });
            }

            if (newNodesAdded.length > 0) {
                enqueueSnackbar(`Added new node #${newNodesAdded.join(', #')} successfully.`, {
                    action: (key) => <SnackbarDismiss key={key} />,
                    variant: 'success',
                });
            }
        }
        const updatedProcedureJson = result.payload.procedure;

        const updatedSortedNodes = sortNodes(updatedProcedureJson.nodes);

        setSortedNodes(updatedSortedNodes);

        setFlowData(
            generateProcedureFlow(
                getEffectiveStartNodeId(updatedProcedureJson),
                updatedSortedNodes,
                treeRanker
            )
        );        

        // Activate the first duplicated node so the user sees it immediately in the node view.
        const firstDuplicatedId = newNodesAdded[0];
        const duplicatedNode = updatedSortedNodes.find((n) => n?.id === firstDuplicatedId);
        if (duplicatedNode) {
            setActiveNode(duplicatedNode);
            setSelectedNodeId(duplicatedNode.id);
            setOpenNodeEditForm(true);
        } else if (activeNode?.id) {
            const refreshedActiveNode = updatedSortedNodes.find((n) => String(n?.id) === String(activeNode.id));
            if (refreshedActiveNode) {
                setActiveNode(refreshedActiveNode);
                setSelectedNodeId(refreshedActiveNode.id);
                setOpenNodeEditForm(true);
            } else {
                setActiveNode(null);
                setOpenNodeEditForm(false);
            }
        }
        if (newNodesTypes.includes('branch') === true) {
            fetchAsset();
        }
        dispatch(setOriginalProcedureData(updatedProcedureJson));
        if (newNodesAdded.length === 0) {
            enqueueSnackbar('No valid titled node selected for duplication.', {
                action: (key) => <SnackbarDismiss key={key} />,
                variant: 'info',
            });
        }
        dispatch(deactivateLoading());
    };

    const handleNodesChanged = useCallback(
        async (nodes, updateStartNodeFromFirstSequence = true) => {
            if (isProcedureDirty === true) {
                setOpenSaveDataDialog(true);
                return;
            }

            const currentProcedure = unsavedProcedure ?? procedure;

            const orderedNodes = sortNodes(
                Array.isArray(nodes) ? [...nodes] : []
            );

            const currentOrderedNodes = sortNodes([
                ...(currentProcedure?.nodes ?? []),
            ]);

            const currentFirstNodeId = currentOrderedNodes[0]?.id ?? null;
            const newFirstNodeId = orderedNodes[0]?.id ?? null;

            const firstSequenceChanged =
                String(currentFirstNodeId ?? '') !==
                String(newFirstNodeId ?? '');

            const startNodeId =
                updateStartNodeFromFirstSequence && firstSequenceChanged
                    ? newFirstNodeId
                    : getValidatedStartNodeId(
                        currentProcedure?.start_node_id,
                        orderedNodes
                    );

            const updatedProcedure = {
                ...currentProcedure,
                start_node_id: startNodeId,
                nodes: nodes,
            };

            dispatch(activateLoading());
            const result = await dispatch(
                updateProcedure({
                    header_image_url: currentProcedure?.header_image_url,
                    procedure_id: procedureId,
                    procedure_json: JSON.stringify(
                        await setHeaderVariables(updatedProcedure)
                    ),
                    asset_id: assetId,
                })
            );
            dispatch(deactivateLoading());
            if (result.error) {
                enqueueSnackbar(result.error, {
                    action: (key) => <SnackbarDismiss key={key} />,
                    variant: 'error',
                });
                return;
            }

            // Update state based on return data from server
            const updatedProcedureJson = JSON.parse(result.payload.procedure_json);

            const updatedSortedNodes = sortNodes(updatedProcedureJson.nodes);

            setSortedNodes(updatedSortedNodes);

            setFlowData(
                generateProcedureFlow(
                    getEffectiveStartNodeId(updatedProcedureJson),
                    updatedSortedNodes,
                    treeRanker
                )
            );

            if (activeNode?.id) {
                const refreshedActiveNode = updatedSortedNodes.find((node) => String(node?.id) === String(activeNode.id));
                if (refreshedActiveNode) {
                    setActiveNode(refreshedActiveNode);
                    setSelectedNodeId(refreshedActiveNode.id);
                } else {
                    setActiveNode(null);
                    setOpenNodeEditForm(false);
                }
            }
            dispatch(setOriginalProcedureData(updatedProcedureJson));
    }, [activeNode?.id, assetId, dispatch, enqueueSnackbar, isProcedureDirty, procedure, unsavedProcedure, procedureId]);

    const handleCancel = () => {
        dispatch(setProcedureDirty(false));
        dispatch(setOriginalProcedureData(procedure));
        setOpenNodeEditForm(false);
        setActiveNode(null);
    };

    // Used only in two-panel mode for EXISTING nodes (isCreatingNode is false).
    const handleCancelExistingNode = () => {
        dispatch(setProcedureDirty(false));
        dispatch(setOriginalProcedureData(procedure));
        // Intentionally does NOT call setActiveNode(null) or setOpenNodeEditForm(false) —
    };

    const handlePropsSubmit = async (procedure) => {
        dispatch(activateLoading({ showProgress: true }));
        if (procedure?.split_view_mode) {
            procedure = {
                ...procedure,
                split_view_mode: +procedure.split_view_mode,
            };
        }
        const updatedProcedure = { ...procedure };
        if (updatedProcedure.header_image) {
            delete updatedProcedure?.image;
            delete updatedProcedure?.image_url;
            delete updatedProcedure?.image_name;
            delete updatedProcedure?.content_data;
            delete updatedProcedure?.header_image;
            delete updatedProcedure?.header_image_url;
            delete updatedProcedure?.header_image_name;
        }
        // Make sure we have the latest nodes from the server
        const serverProcedure = await dispatch(
            fetchProcedure({
                service: procedureService,
                procedureId: procedureId,
            })
        );

        const serverProcedureJson = serverProcedure.payload.procedure;
        // assetService.updateProcedureNodes(serverProcedureJson);
        updatedProcedure.nodes = serverProcedureJson.nodes;

        updatedProcedure.start_node_id = getValidatedStartNodeId(
            updatedProcedure.start_node_id,
            updatedProcedure.nodes
        );        

        if (procedure?.header_image) {
            delete procedure?.header_image_url;
        }
        const result = await dispatch(
            updateProcedure({
                header_image_url: procedure?.header_image_url,
                header_image: procedure?.header_image,
                procedure_id: procedureId,
                procedure_json: JSON.stringify({
                    ...updatedProcedure,
                }),
                asset_id: assetId,
            })
        );

        if (result.error) {
            dispatch(deactivateLoading());
            enqueueSnackbar(result.error, {
                action: (key) => <SnackbarDismiss key={key} />,
                variant: 'error',
            });
            return;
        }
        const updatedProcedureJson = result.payload.procedure;

        const updatedSortedNodes = sortNodes(updatedProcedureJson.nodes);

        setSortedNodes(updatedSortedNodes);

        setFlowData(
            generateProcedureFlow(
                getEffectiveStartNodeId(updatedProcedureJson),
                updatedSortedNodes,
                treeRanker
            )
        );

        setShowEditDialog(false);
        dispatch(deactivateLoading());
        dispatch(setOriginalProcedureData(updatedProcedureJson));
    };

    const handlePropsCancel = () => {
        setShowEditDialog(false);
    };

    const handlePreview = async () => {
        const previewProcedure = unsavedProcedure ?? procedure;
        const startNodeId = getEffectiveStartNodeId(previewProcedure);        
        dispatch(setProcedureState(previewProcedure));
        dispatch(setChecklistInitialState());

        if (startNodeId !== null) {
            const startNode = previewProcedure?.nodes?.find(
                (node) => String(node?.id) === String(startNodeId)
            );

            dispatch(gotoNode(Number(startNodeId)));
            emitNodeInitialView(startNode, previewProcedure);
        }

        setPreviewMode(true);
    };

    const handleNodePreview = useCallback(({ procedure, node }) => {
        if (isProcedureDirty === true) {
            setOpenSaveDataDialog(true);
            return;
        }
        dispatch(setProcedureState(procedure));
        dispatch(setChecklistInitialState());
        dispatch(gotoNode(node.id));
        emitNodeInitialView(node, procedure);
        setPreviewMode(true);
    }, [dispatch, isProcedureDirty, procedure]);

    const handleClose = () => {
        setPreviewMode(false);
        dispatch(setDuplicateFields({}));
    };

    const togglePhoneWidth = () => {
        setPhoneWidth(!phoneWidth);
        if (!phoneWidth) {
            setPreviewWidth(500);
        } else {
            setPreviewWidth(parentWidth);
        }
    };

    const handleEditProperties = () => {
        setShowEditDialog(true);
    };

    const updateProcedureData = useCallback(
        async (updatedNodes, updatedNodeCategories) => {
            const currentProcedure = unsavedProcedure ?? procedure;

            const updatedProcedure = {
                ...currentProcedure,
                procedureCategories: [...updatedNodeCategories],
                nodes: updatedNodes,
                start_node_id: getValidatedStartNodeId(
                    currentProcedure?.start_node_id,
                    updatedNodes
                ),
            };

            dispatch(activateLoading());
            const result = await dispatch(
                updateProcedure({
                    header_image_url: currentProcedure?.header_image_url,
                    procedure_id: procedureId,
                    procedure_json: JSON.stringify(
                        await setHeaderVariables(updatedProcedure)
                    ),
                    asset_id: assetId,
                })
            );
            if (result.error) {
                dispatch(deactivateLoading());
                enqueueSnackbar(result.error.message, {
                    action: (key) => <SnackbarDismiss key={key} />,
                    variant: 'error',
                });
                return;
            }
            setOpenCategoryDialog(false);
            dispatch(deactivateLoading());
            const updatedProcedureJson = result.payload.procedure;

            const updatedSortedNodes = sortNodes(updatedProcedureJson.nodes);

            setSortedNodes(updatedSortedNodes);
            setFlowData(generateProcedureFlow(getEffectiveStartNodeId(updatedProcedureJson), updatedSortedNodes, treeRanker));
            const updatedCurrentNode = updatedNodes?.find((node) => node.id === activeNode?.id);
            if (updatedCurrentNode && Object.keys(updatedCurrentNode || {}).length > 0) {
                setActiveNode(updatedCurrentNode);
            }
            dispatch(setOriginalProcedureData(updatedProcedureJson));
        },
        [activeNode?.id, assetId, dispatch, enqueueSnackbar, procedure, unsavedProcedure, procedureId]
    );
    //#endregion Methods

    //#region Render time calcs
    // Memoize procedureCategories so NodeEditor and all child forms receive a stable
    // reference — prevents re-renders / effect re-runs whenever ProcedureDesigner
    // re-renders for unrelated reasons (e.g. openSaveDataDialog toggle).
    const procedureCategories = useMemo(
        () => procedure?.procedureCategories ?? [],
        // Only recompute when the categories array identity changes on the procedure prop.
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [procedure?.procedureCategories]
    );

    const nodeDisplay = useMemo(() => (
        <NodeManagerDisplay
            showNodeFlow={showNodeFlow}
            flowData={flowData}
            selectedNodeId={selectedNodeId}
            handleNodeSelected={handleNodeSelected}
            procedure={unsavedProcedure}
            sortedNodes={sortedNodes}
            showTwoPanelMode={showTwoPanelMode}
            handleNodeClick={handleNodeClick}
            handleNodePreview={handleNodePreview}
            handleNodesChanged={handleNodesChanged}
            handleNodeDuplicate={handleNodeDuplicate}
            setClickedAddButton={handleSetClickedAddButton}
            compactMode={compactMode}
            showBranches={showBranches}
            setOpenSaveDataDialog={setOpenSaveDataDialog}
            showDeleteNodeDialog={showDeleteNodeDialog}
            setComponentMethodsRef={setComponentMethodsRef}
            setOpenFlowSettingsDialog={setOpenFlowSettingsDialog}
            nodeDisplayPanel={nodeDisplayPanel}
            isCreatingNode={isCreatingNode}
        />
    ), [
        showNodeFlow, flowData, selectedNodeId, handleNodeSelected, unsavedProcedure,
        sortedNodes, showTwoPanelMode, handleNodeClick, handleNodePreview,
        handleNodesChanged, handleNodeDuplicate, handleSetClickedAddButton,
        compactMode, showBranches, showDeleteNodeDialog, isCreatingNode,
    ]);
    //#endregion Render time calcs

    //#region Render
    return (
        <>
            {/* Mount the listener so scene "Set Initial View" clicks update the node in the procedure store */}
            <NodeInitialViewListener />
            {/* <div className={procedureStyles.container}> */}
            <Tooltip title="Preview">
                <span style={{ cursor: 'pointer', display: 'inline-flex' }} onClick={handlePreview}>
                    <MdPreview className="react-icon" />
                </span>
            </Tooltip>
            {readOnlyMode === true ? (
                <NodeTableViewOnly procedure={procedure} nodes={sortedNodes} />
            ) : (
                <>
                    <NodeManagerOptions
                        onAddNode={handleAddNodeWithConfirm}
                        activeNode={activeNode}
                        sortedNodes={sortedNodes}
                        clickedAddButton={clickedAddButton}
                        setClickedAddButton={handleSetClickedAddButton}
                        onEditProperties={handleEditProperties}
                        compactMode={compactMode}
                        setCompactMode={setCompactMode}
                        showBranches={showBranches}
                        setShowBranches={setShowBranches}
                        showNodeFlow={showNodeFlow}
                        setShowNodeFlow={setShowNodeFlow}
                        showTwoPanelMode={showTwoPanelMode}
                        setShowTwoPanelMode={setShowTwoPanelMode}
                        setOpenSaveDataDialog={setOpenSaveDataDialog}
                        setOpenNodeEditForm={setOpenNodeEditForm}
                        setOpenCategoryDialog={setOpenCategoryDialog}
                    />
                    {showTwoPanelMode ? (
                        <div id="grid" className={procedureStyles.twoPanelMode}>
                            <div ref={nodeDisplayPanel} className={procedureStyles.nodeDisplayPanel}>
                                {nodeDisplay}
                            </div>
                            <div className={procedureStyles.panelResizer}>
                                <PanelResize previousPanelRef={nodeDisplayPanel} nextPanelRef={nodePreviewPanel} />
                            </div>
                            <div ref={nodePreviewPanel} className={procedureStyles.nodePreviewPanel}>
                                {showTwoPanelMode === true && activeNode && (
                                    <NodeEditor
                                        activeNode={activeNode}
                                        assetId={assetId}
                                        variables={variables}
                                        sortedNodes={sortedNodes}
                                        handleSubmit={handleSubmit}
                                        handleCancel={
                                            isCreatingNode
                                                ? handleCreateModeCancel
                                                : handleCancelExistingNode
                                        }
                                        openSaveDataDialog={openSaveDataDialog}
                                        setOpenSaveDataDialog={setOpenSaveDataDialog}
                                        showTwoPanelMode={showTwoPanelMode}
                                        procedureCategories={procedureCategories}
                                    />
                                )}
                            </div>
                        </div>
                    ) : (
                        <div
                            ref={nodeDisplayPanel}
                            className={procedureStyles.nodeDisplayPanel}
                            style={{ borderTop: '1px solid' }}
                        >
                            {nodeDisplay}
                        </div>
                    )}
                </>
            )}
            {showTwoPanelMode === false && activeNode && (
                <Dialog
                    fullScreen={false}
                    open={openNodeEditForm}
                    fullWidth
                    maxWidth="xl"
                    disableEnforceFocus={true}
                    onClose={(e, reason) => {
                        if (reason && (reason === 'backdropClick' || reason === 'escapeKeyDown')) {
                            if (isProcedureDirty) {
                                setOpenSaveDataDialog(true);
                            } else {
                                handleCancel();
                            }
                        }
                    }}
                >
                    <DialogContent>
                        <NodeEditor
                            activeNode={activeNode}
                            assetId={assetId}
                            variables={variables}
                            sortedNodes={sortedNodes}
                            handleSubmit={handleSubmit}
                            handleCancel={handleCancel}
                            openSaveDataDialog={openSaveDataDialog}
                            setOpenSaveDataDialog={setOpenSaveDataDialog}
                            showTwoPanelMode={showTwoPanelMode}
                            procedureCategories={procedureCategories}
                        />
                    </DialogContent>
                </Dialog>
            )}
            {showEditDialog && (
                <Dialog open={true} fullWidth>
                    <DialogContent>
                        <div className={procedureStyles.formContainer}>
                            <ProcedurePropsForm
                                procedure={procedure}
                                variables={variables}
                                onSubmit={handlePropsSubmit}
                                onCancel={handlePropsCancel}
                            />
                        </div>
                    </DialogContent>
                </Dialog>
            )}
            {previewMode && (
                <Dialog
                    fullScreen={false}
                    open={previewMode}
                    onClose={handleClose}
                    maxWidth="xl"
                    classes={{ paper: procedureStyles.paper }}
                >
                    <ResizableBox
                        width={previewWidth}
                        height={parentHeight}
                        className={resizableStyles.resizable}
                        style={{
                            maxWidth: '100%',
                            minHeight: '500px',
                            minWidth: '400px',
                        }}
                    >
                        <>
                            <DialogTitle
                                sx={{
                                    fontSize: '12px',
                                    display: 'flex',
                                    alignItems: 'center',
                                }}
                            >
                                <div>{`Node Id ${currentNode?.id}`}</div>
                                <MdPhoneIphone
                                    className="react-icon"
                                    style={{
                                        cursor: 'pointer',
                                        marginLeft: 'auto',
                                        color: phoneWidth ? 'blue' : 'unset',
                                    }}
                                    onClick={togglePhoneWidth}
                                />
                                <MdClose className="react-icon" onClick={handleClose} />
                            </DialogTitle>
                            <DialogContent style={{ height: 'calc(100% - 56px)' }}>
                                <Procedure
                                    procedureService={procedureService}
                                    contentService={contentService}
                                    useZoneNav={useZoneNav}
                                />
                            </DialogContent>
                        </>
                    </ResizableBox>
                </Dialog>
            )}
            {openDeleteConfirmDialog && (
                <Dialog
                    open={openDeleteConfirmDialog}
                    onClose={closeDeleteConfirmDialog}
                    aria-labelledby="alert-dialog-title"
                    aria-describedby="alert-dialog-description"
                >
                    <DialogTitle id="alert-dialog-title">{'Delete Node'}</DialogTitle>
                    <DialogContent>
                        <DialogContentText id="alert-dialog-description">
                            {`Are you sure you want to delete node #${
                                Array.isArray(selectedDeleteNode) ? selectedDeleteNode.join(', #') : selectedDeleteNode
                            }?`}
                        </DialogContentText>
                    </DialogContent>
                    <DialogActions>
                        <Button onClick={closeDeleteConfirmDialog}>Cancel</Button>
                        <Button autoFocus variant="contained" color="error" onClick={handleNodeDelete}>
                            Confirm
                        </Button>
                    </DialogActions>
                </Dialog>
            )}

            {/* Duplicate Node Dialog */}
            <DuplicateNodeDialog
                open={openDuplicateDialog}
                onClose={() => {
                    setOpenDuplicateDialog(false);
                    setPendingDuplicateIds(null);
                }}
                onConfirm={handleDuplicateConfirm}
                nodeCount={pendingDuplicateIds?.length || 1}
            />

            {openInsertWarningDialog && (
                <Dialog
                    open={openInsertWarningDialog}
                    onClose={cancelInsert}
                    aria-labelledby="insert-warning-title"
                    aria-describedby="insert-warning-description"
                    PaperProps={{
                        sx: { borderRadius: 2, minWidth: 360, maxWidth: 480 },
                    }}
                >
                    <DialogTitle id="insert-warning-title">Add Node</DialogTitle>
                    <DialogContent>
                        <DialogContentText id="insert-warning-description">
                            This action could impact or be impacted by a non-visible node. Do you want to continue?
                        </DialogContentText>
                    </DialogContent>
                    <DialogActions>
                        <Button onClick={cancelInsert}>No</Button>
                        <Button autoFocus variant="contained" onClick={confirmInsert}>
                            Yes
                        </Button>
                    </DialogActions>
                </Dialog>
            )}
            {showNodeFlow === true && openFlowSettingsDialog && (
                <ProcedureFlowSettings openDialog={openFlowSettingsDialog} setOpenDialog={setOpenFlowSettingsDialog} />
            )}

            <NodeCategoriesModal
                procedure={procedure}
                openDialog={openCategoryDialog}
                setOpenDialog={setOpenCategoryDialog}
                updateProcedureData={updateProcedureData}
            />

            {/* Step 1: pick a node type when + is clicked in tree view */}
            <NodeTypeButton
                openDialog={openTreeViewNodeTypeDialog}
                setOpenDialog={(open) => {
                    setOpenTreeViewNodeTypeDialog(open);
                    if (!open) setTreeViewPendingInsertIdx(null);
                }}
                clickedAddButton={false}
                setClickedAddButton={() => {}}
                handleButtonSelection={handleTreeViewNodeTypeSelected}
            />

            {/* Step 2: fill in the node form before placing in tree */}
            {openTreeViewNodeEditDialog && activeNode && (
                <Dialog
                    fullScreen={false}
                    open={openTreeViewNodeEditDialog}
                    fullWidth
                    maxWidth="md"
                    disableEnforceFocus={true}
                    onClose={(e, reason) => {
                        if (reason && (reason === 'backdropClick' || reason === 'escapeKeyDown')) {
                            if (isProcedureDirty) {
                                setOpenSaveDataDialog(true);
                            } else {
                                handleCreateModeCancel();
                            }
                        }
                    }}
                >
                    <DialogContent>
                        <NodeEditor
                            activeNode={activeNode}
                            assetId={assetId}
                            variables={variables}
                            sortedNodes={sortedNodes}
                            handleSubmit={handleSubmit}
                            handleCancel={handleCreateModeCancel}
                            openSaveDataDialog={openSaveDataDialog}
                            setOpenSaveDataDialog={setOpenSaveDataDialog}
                            showTwoPanelMode={true}
                            procedureCategories={procedureCategories}
                        />
                    </DialogContent>
                </Dialog>
            )}
        </>
    );
    //#endregion Render
};

export default ProcedureDesigner;